import { type ImgHTMLAttributes } from 'react'

// Logo PNGs live in public/ (so they're served at the URL literal below) and
// are imported as string literals instead of `import x from '@/assets/...'`
// so the file is importable from Node-side SSR (scripts/prerender.tsx).
// tsx/esbuild does not understand Vite's `?url` asset transform; bare-path
// imports of hashed assets from src/assets/ break the prerender pass with
// ERR_UNKNOWN_FILE_EXTENSION.
//
// The 3 PNGs were copied from src/assets/brand/ → public/ on 2026-10-14; the
// src/assets/ copies are now stale (Vite still copies them to dist/assets/
// but they're no longer referenced from source). A future cleanup can move
// them to public/ permanently and delete src/assets/brand/.
//
// @see scripts/prerender.tsx — per-route SSR render of <App /> which mounts <Logo>
const logoHorizontal = '/yada-logo-horizontal.png'
const logoVertical = '/yada-logo-vertical.png'
const logoIcon = '/yada-icon.png'

type LogoVariant = 'horizontal' | 'icon' | 'stacked'
type LogoSize = 'xs' | 'sm' | 'md' | 'lg' | 'navbar'

interface LogoProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  variant?: LogoVariant
  size?: LogoSize
  /**
   * Render eagerly (no `loading="lazy"`).
   * Set true for above-the-fold surfaces: navbar, footer, hero, About brand.
   * Inline references (related-content, dropdown thumbs) leave it false.
   */
  eager?: boolean
  alt?: string
}

/**
 * Asset map — single import location for every brand PNG.
 * Intrinsic dimensions come from `file`:
 *   horizontal: 4725×1654 (~2.86:1)  — primary lockup, used everywhere
 *   stacked:    4725×3307 (~1.43:1)  — alternative wide lockup
 *   icon:       2835×2363 (~1.20:1)  — square-ish, used in admin + tight UIs
 */
const ASSETS: Record<LogoVariant, string> = {
  horizontal: logoHorizontal,
  stacked: logoVertical,
  icon: logoIcon,
}

const DIMENSIONS: Record<LogoVariant, { w: number; h: number }> = {
  horizontal: { w: 4725, h: 1654 },
  stacked: { w: 4725, h: 3307 },
  icon: { w: 2835, h: 2363 },
}

/**
 * Size map — Tailwind classes per (size, breakpoint).
 * Phase 1 mobile overhaul (2026-10-13):
 *   - xs  →  40px          Admin navbar (tight chrome; bumped from 36px
 *                          2026-10-15 so the lockup reads at the same
 *                          weight as the rest of the header row)
 *   - sm  →  56px → 80px   (was the public navbar's step until 2026-10-18.
 *                          2.86:1 lockup needs weight on 414px viewports;
 *                          mobile 40 → 48 → 56 over two rounds, then
 *                          desktop 64 → 80 against the 110 px sticky row)
 *   - md  →  48px → 56px   Footer
 *   - lg  →  56px → 80px   About hero / brand wall
 *
 *   - navbar → 76px → 80px Public navbar as of 2026-10-18. This is a SEPARATE
 *                          step rather than a bump to `sm` on purpose: `sm` is
 *                          the shared step and raising it would have silently
 *                          resized every other `sm` consumer. The 80 px arm
 *                          used to fire at `md`, but that disagreed with the
 *                          primary-nav gate (`xl`): between 768 and 1279 the
 *                          header kept a desktop-sized lockup next to the
 *                          hamburger and read as a scaled desktop. The 80 px
 *                          arm now matches `xl` — same gate as the 110 px
 *                          row and the inline nav. Requires the navbar row
 *                          to be `h-[88px]` below xl — see Navbar.tsx and
 *                          `--sticky-header-offset`.
 *
 *   Mobile height is a `clamp`, not a fixed step. The lockup is 2.857:1, so
 *   76 px tall is 217 px wide. With the flag (44 px) + gap (8) + hamburger
 *   (44) the navbar row needs 313 px of content width, and a 320 px viewport
 *   (iPhone SE 1st gen) only offers 292 px after `px-section-x` — the Logo's
 *   parent <Link> is `flex-shrink-0`, so `justify-between` would NOT absorb
 *   the overflow; the logo would just sit under the hamburger. So:
 *     - ≥375 px → 76 px, the intended size
 *     - <375 px → scales down toward the 4rem floor
 *   Every phone shipping in 2026 is ≥360 px, so the clamp's lower branch is
 *   insurance for a device that is functionally extinct, not a design state.
 *   `vh`-based math is deliberately avoided: `vmin` would make the logo
 *   shorter on SHORT landscape screens, where the row is narrowest.
 *
 *   A note on why 76 px needs an 88 px row rather than an 80 px one. The PNG
 *   carries 15.5% baked-in vertical padding (measured: ink occupies rows
 *   256…1396 of 1654), so the CSS box's apparent padding is not what you see.
 *   In an 80 px row, 76 px of CSS box leaves only 2 px outside the artwork and
 *   ~13.8 px of apparent gap; at 88 px it is 6 px + ~17.8 px, which matches the
 *   proportions the original 64 px-row / 56 px-logo design had. Measuring the
 *   artwork is the only way to get this right — the intrinsic 4725×1654 ratio
 *   is the FULL box including that padding, not the visible mark.
 */
const SIZE_CLASSES: Record<LogoSize, string> = {
  xs: 'h-10 w-auto',
  sm: 'h-14 w-auto md:h-20 md:w-auto',
  md: 'h-12 w-auto md:h-14 md:w-auto',
  lg: 'h-14 w-auto md:h-20 md:w-auto',
  navbar: 'h-[clamp(4.25rem,20.3vw,4.75rem)] w-auto xl:h-20 xl:w-auto',
}

/**
 * Logo — single source of truth for every brand mark on the site.
 * Replaces 3 inline `<img>` copies that were scattered across
 * Navbar (h-[70px] md:h-[90px]), Footer (h-20), AdminApp (h-9 sm:h-10).
 *
 * Width/height attributes are set to the intrinsic PNG pixel size so the
 * browser reserves a correct aspect-ratio box before CSS kicks in,
 * preventing CLS (cumulative layout shift) on navbar/footer paint.
 */
export default function Logo({
  variant = 'horizontal',
  size = 'sm',
  eager = false,
  alt = '青年耀進發展協會 YADA',
  className = '',
  ...rest
}: LogoProps) {
  const dim = DIMENSIONS[variant]
  return (
    <img
      src={ASSETS[variant]}
      alt={alt}
      width={dim.w}
      height={dim.h}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      className={[SIZE_CLASSES[size], className].filter(Boolean).join(' ')}
      {...rest}
    />
  )
}
